import { useNavigate } from 'react-router';

import { useAuth } from './useAuth';

/**
 * Sign in, then go on to the business form - the half of signing up that
 * happens once the account is real and its address is proved.
 *
 * Lives here, not on the register page, because it runs on the *verify* page:
 * registration hands over to `/verify-email` the moment the account exists, and
 * this is what that page does with an accepted code.
 *
 * The business is not created here. The register page asks only for its name,
 * and a business made from a name alone has no industry - so it starts with an
 * empty catalog, and anything seeded later is the general shop's tote bags
 * rather than the clinic's consultations. The onboarding form asks for the
 * industry and the starter samples, reads back the name the register page
 * remembered, and carries on to the plan from there.
 *
 * Resolves to an error to show, or null once it has navigated away.
 */
export function useFinishSignup() {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  return async (email: string, password: string): Promise<string | null> => {
    // Signing in is a separate step with a separate failure, and it happens
    // after the account exists. Folding it into the caller's try would answer a
    // rate-limited or briefly-unavailable sign-in with "something went wrong" -
    // which reads as "your account was not created", sends the merchant round
    // again, and lands them on EMAIL_ALREADY_REGISTERED for an account that is
    // theirs.
    try {
      await signIn(email, password);
    } catch {
      return 'Your account is ready. Please sign in to continue.';
    }

    void navigate('/onboarding', { replace: true });
    return null;
  };
}
